import axios from "axios";
import { useEffect, useState } from "react";
import { Loader2, Save, Bell, Mail } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const NotificationPrefsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [labels, setLabels] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [p, opts] = await Promise.all([
        axios.get(`${API}/notifications/prefs`),
        axios.get(`${API}/notifications/prefs/options`),
      ]);
      setPrefs(p.data);
      setLabels(opts.data?.labels || {});
    } catch {
      toast.error("Erro ao carregar preferências");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/notifications/prefs`, {
        email_enabled: prefs.email_enabled,
        by_type: prefs.by_type,
      });
      setPrefs(res.data);
      toast.success("Preferências salvas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" /> Carregando…
      </div>
    );
  }

  if (!prefs) return null;

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
        <div>
          <h2 className="heading text-lg font-semibold text-slate-900 mb-1">
            Notificações por e-mail
          </h2>
          <p className="text-sm text-slate-500">
            O sino do GridRecruiter sempre te avisa de eventos importantes. Aqui
            você escolhe quais desses eventos também chegam por e-mail.
          </p>
        </div>

        {/* Master switch */}
        <div className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3 bg-slate-50">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-blue-50 text-brand-blue flex items-center justify-center shrink-0">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">
                Receber alertas por e-mail
              </p>
              <p className="text-xs text-slate-500">
                Desligando aqui você desabilita TODOS os e-mails de
                notificação (in-app continua funcionando).
              </p>
            </div>
          </div>
          <Switch
            data-testid="email-enabled-switch"
            checked={!!prefs.email_enabled}
            onCheckedChange={(v) =>
              setPrefs((p) => ({ ...p, email_enabled: v }))
            }
          />
        </div>

        {/* Per-type toggles */}
        <div
          className={`space-y-2 ${
            !prefs.email_enabled ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Quais eventos quero por e-mail
          </h3>
          {Object.keys(labels).map((type) => (
            <label
              key={type}
              data-testid={`pref-row-${type}`}
              className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-2.5 cursor-pointer hover:bg-slate-50"
            >
              <div className="flex items-center gap-2">
                <Bell className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-sm text-slate-800">{labels[type]}</span>
              </div>
              <Switch
                checked={!!prefs.by_type?.[type]}
                onCheckedChange={(v) =>
                  setPrefs((p) => ({
                    ...p,
                    by_type: { ...(p.by_type || {}), [type]: v },
                  }))
                }
                data-testid={`pref-switch-${type}`}
              />
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-brand-blue hover:bg-brand-blue-dark text-white"
          data-testid="save-prefs-button"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-1.5" />
          )}
          Salvar preferências
        </Button>
      </div>
    </div>
  );
};

export default NotificationPrefsPanel;
